import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Users, Database, Shield, FileText, Cookie,
  Activity, BarChart3, Settings, ChevronLeft, ChevronDown, ChevronRight,
  Menu, Globe, Bell, AlertTriangle, Tag, X, LogOut, Building2,
  ClipboardCheck, ShieldCheck, Layers,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUpcomingReview } from '../../modules/privilege-review/hooks/usePrivilegeReview';
import DEFERRED_SURFACES from '../../config/deferredSurfaces';

const PINNED_ITEMS = [
  { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', permission: 'view_dashboard' },
];

const NAV_GROUPS = [
  {
    key: 'dataManagement',
    labelKey: 'nav.group.dataManagement',
    items: [
      { path: '/discovery', icon: Database, labelKey: 'nav.dataDiscovery', permission: 'view_data_sources' },
      { path: '/minimization', icon: ShieldCheck, labelKey: 'nav.dataMinimization', permission: 'view_data_sources' },
      { path: '/classification', icon: Tag, labelKey: 'nav.dataClassification', permission: 'view_classifications' },
      { path: '/review-center', icon: ClipboardCheck, labelKey: 'nav.reviewCenter', permission: 'view_classifications' },
      { path: '/dataflow', icon: Activity, labelKey: 'nav.dataFlow', permission: 'view_data_flows' },
    ],
  },
  {
    key: 'compliance',
    labelKey: 'nav.group.compliance',
    items: [
      { path: '/pdpl-assessment', icon: Shield, labelKey: 'nav.pdplClassification', permission: 'view_compliance' },
      // GH #164 — Policy Management module deferred; flip
      // DEFERRED_SURFACES.SHOW_POLICY_MANAGEMENT to restore this nav entry.
      ...(DEFERRED_SURFACES.SHOW_POLICY_MANAGEMENT
        ? [{ path: '/policy-management', icon: ShieldCheck, labelKey: 'nav.policyManagement', permission: 'view_compliance' }]
        : []),
      { path: '/assessment-center', icon: FileText, labelKey: 'nav.assessmentCenter', permission: 'view_compliance' },
      { path: '/ropa', icon: FileText, labelKey: 'nav.ropa', permission: 'view_ropa' },
      // Spec 024 — DPO purpose curation queue. dpo_oversight scopes
      // visibility to DPO + Admin (matches the backend list route);
      // mutation buttons inside the page additionally require
      // curate_purpose_proposals.
      { path: '/ropa/purpose-curation', icon: Layers, labelKey: 'nav.purposeCuration', permission: ['dpo_oversight', 'view_purpose_curation'] },
      // 053 G3 (T287, owner ruling 2026-08-19) — the standalone "Privacy
      // Notices" nav entry is REMOVED. Notice work is reached from the step
      // whose duty creates it: the requirement-register row's notice-coverage
      // action, the ROPA detail basis panel's covering-notice link, the
      // Evidence tab's notice inventory, and the deep-linked notice-gap
      // DPOTask. The /privacy-notices ROUTE stays mounted in App.js behind
      // its view_ropa GuardedRoute — it is a deep-link TARGET, not a
      // destination you browse to. Do NOT "restore" this entry: reinstating
      // it re-creates the two-front-doors problem G3 closed (D-R10).
      { path: '/dpta', icon: ShieldCheck, labelKey: 'nav.dpta', permission: 'view_dpta' },
      // Command Center is a tab INSIDE the ROPA hub (see ROPAPage.js
      // TAB_KEYS) — it does not need its own sidebar entry. The
      // /ropa/command-center route stays registered in App.js for
      // direct-URL access from the Phase 16 dashboard build, but we
      // do not advertise it in the nav since that produced two
      // visibly-identical ROPA entries.
    ],
  },
  {
    key: 'riskBreach',
    labelKey: 'nav.group.riskBreach',
    items: [
      // GH #165 — Risk Register module deferred; flip
      // DEFERRED_SURFACES.SHOW_RISK_REGISTER to restore this nav entry.
      ...(DEFERRED_SURFACES.SHOW_RISK_REGISTER
        ? [{ path: '/risk', icon: Shield, labelKey: 'nav.riskRegister', permission: 'view_risks' }]
        : []),
      { path: '/breaches', icon: AlertTriangle, labelKey: 'nav.breachManagement', permission: 'view_breaches' },
      // GH #166 — Breach Notifications module deferred; flip
      // DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS to restore this nav entry.
      ...(DEFERRED_SURFACES.SHOW_BREACH_NOTIFICATIONS
        ? [{ path: '/breach-notifications', icon: Bell, labelKey: 'nav.breachNotifications', permission: 'notify_breach' }]
        : []),
    ],
  },
  {
    key: 'privacyOps',
    labelKey: 'nav.group.privacyOps',
    items: [
      { path: '/dsr', icon: FileText, labelKey: 'nav.dsrManagement', permission: 'view_dsr' },
      { path: '/consent', icon: Cookie, labelKey: 'nav.consent', permission: 'view_consent' },
      // 053 G3 (T287, owner ruling 2026-08-19) — the standalone "Identity
      // Queue" nav entry is REMOVED. Identity review lives on the Records
      // surface: the "identity needs review (N)" segment and the per-record
      // identity flags open IdentityLinkSlideOver in place (T272), and the
      // in-context receiver reads /consent?identityLink=<id>. The
      // /consent/identity-queue ROUTE stays mounted in App.js behind its
      // dpo_oversight GuardedRoute — it is the deep-link target the two
      // identity DPOTask writers still address (service.py:1582,
      // resolution.py:271). Do NOT "restore" this entry (D-R10).
      // consent/import and consent/purpose-curation are hidden from nav (accessible via ConsentPage directly)
      { path: '/dpo', icon: Shield, labelKey: 'nav.dpoManagement', permission: 'dpo_oversight' },
      // Command Center is a sub-route of the DPO hub — not surfaced in nav
      // (consistent with ROPA command-center pattern). Route remains in App.js.
      { path: '/scanner', icon: Globe, labelKey: 'nav.websiteScanner', permission: 'scan_websites' },
    ],
  },
  {
    key: 'auditReports',
    labelKey: 'nav.group.auditReports',
    items: [
      { path: '/audit', icon: Activity, labelKey: 'nav.auditLogs', permission: 'view_audit_logs' },
      // GH #167 — Reports module deferred; flip
      // DEFERRED_SURFACES.SHOW_REPORTS to restore this nav entry.
      ...(DEFERRED_SURFACES.SHOW_REPORTS
        ? [{ path: '/reports', icon: BarChart3, labelKey: 'nav.reports', permission: 'view_reports' }]
        : []),
    ],
  },
  {
    key: 'administration',
    labelKey: 'nav.group.administration',
    items: [
      { path: '/users', icon: Users, labelKey: 'nav.users', permission: 'view_users' },
      { path: '/privilege-reviews', icon: ClipboardCheck, labelKey: 'nav.privilegeReviews', permission: 'view_privilege_reviews' },
      { path: '/organization', icon: Building2, labelKey: 'nav.organization', permission: 'manage_settings' },
      // Identity Providers moved off the sidebar — now reachable via the
      // "related admin pages" kebab menu on SettingsPage (see settings MODULE.md IA note).
      { path: '/settings', icon: Settings, labelKey: 'nav.settings', permission: 'view_settings' },
    ],
  },
];

const STORAGE_KEY = 'privacyguard:sidebar-groups';
const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { logout, hasPermission } = useAuth();
  const { upcoming } = useUpcomingReview();

  // Spec 052 T250 (FR-029/FR-060) — a nav item's `permission` may be a single
  // string OR an array with ANY-OF semantics (mirrors App.js GuardedRoute), so
  // the item shows when the viewer holds at least one of the listed permissions.
  const itemAllowed = (item) => (
    Array.isArray(item.permission)
      ? item.permission.some((p) => hasPermission(p))
      : hasPermission(item.permission)
  );

  // Accordion state — initialize from localStorage or auto-open active group
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    // First visit: expand only the group containing the active route
    const initial = {};
    for (const group of NAV_GROUPS) {
      const hasActive = group.items.some(
        (item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/')
      );
      initial[group.key] = hasActive;
    }
    return initial;
  });

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/' || location.pathname === '/dashboard';
    // Exact match first, then prefix match
    if (location.pathname === path) return true;
    // Only match prefix if no other item has a more specific match
    if (location.pathname.startsWith(path + '/')) {
      // Check if a more specific path exists in any group
      const moreSpecific = ALL_NAV_ITEMS.some(
        (item) => item.path !== path && item.path.startsWith(path + '/') && location.pathname.startsWith(item.path)
      );
      return !moreSpecific;
    }
    return false;
  };

  const handleNavigate = (path) => {
    navigate(path);
    onMobileClose?.();
  };

  const sidebarContent = (
    <>
      {/* Header — matches TopBar h-16 */}
      <div className="h-16 px-4 border-b border-gray-800 flex items-center justify-between shrink-0">
        {!collapsed && (
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-1.5 bg-emerald-600 rounded-lg shrink-0">
              <Shield size={22} />
            </div>
            <span className="font-bold text-base truncate">
              {t('appName')}
            </span>
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-1.5 hover:bg-gray-800 rounded-lg hidden lg:flex items-center justify-center shrink-0"
        >
          {collapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
        </button>
        {mobileOpen && (
          <button
            onClick={onMobileClose}
            className="p-1.5 hover:bg-gray-800 rounded-lg lg:hidden"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-2 overflow-y-auto min-h-0 scrollbar-hide">
        {/* Pinned items */}
        {PINNED_ITEMS.filter(itemAllowed).map((item) => (
          <button
            key={item.path}
            data-nav-path={item.path}
            onClick={() => handleNavigate(item.path)}
            className={`w-full flex items-center py-2.5 transition-colors ${
              collapsed ? 'justify-center' : 'gap-3 px-4'
            } ${
              isActive(item.path)
                ? 'bg-emerald-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
            title={collapsed ? t(item.labelKey) : undefined}
          >
            <item.icon size={18} className="shrink-0" />
            {!collapsed && <span className="text-sm truncate">{t(item.labelKey)}</span>}
          </button>
        ))}

        {/* Divider */}
        <div className={`border-t border-gray-800 my-2 ${collapsed ? 'mx-4' : 'mx-3'}`} />

        {/* Grouped items */}
        {NAV_GROUPS.map((group) => {
          const visibleItems = group.items.filter(itemAllowed);
          if (visibleItems.length === 0) return null;
          const isExpanded = expandedGroups[group.key];

          if (collapsed) {
            // Collapsed mode: render items as icons with subtle group dividers
            return (
              <React.Fragment key={group.key}>
                <div className="border-t border-gray-800/50 my-1 mx-5" />
                {visibleItems.map((item) => {
                  const showDot = item.path === '/privilege-reviews' && upcoming?.review_due;
                  return (
                    <button
                      key={item.path}
                      data-nav-path={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className={`w-full flex items-center justify-center py-2.5 transition-colors ${
                        isActive(item.path)
                          ? 'bg-emerald-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }`}
                      title={t(item.labelKey)}
                    >
                      <span className="relative shrink-0">
                        <item.icon size={18} />
                        {showDot && (
                          <span className="absolute -top-1 -end-1 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-gray-900" />
                        )}
                      </span>
                    </button>
                  );
                })}
              </React.Fragment>
            );
          }

          return (
            <div key={group.key}>
              {/* Section header */}
              <button
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center justify-between px-4 py-2 mt-1 text-gray-400 hover:text-gray-300 transition-colors"
              >
                <span className="text-xs tracking-wider font-semibold uppercase">
                  {t(group.labelKey)}
                </span>
                {isExpanded
                  ? <ChevronDown size={12} className="text-gray-500" />
                  : <ChevronRight size={12} className="text-gray-500" />
                }
              </button>

              {/* Section items */}
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  isExpanded ? 'max-h-96' : 'max-h-0'
                }`}
              >
                {visibleItems.map((item) => {
                  const showDot = item.path === '/privilege-reviews' && upcoming?.review_due;
                  return (
                    <button
                      key={item.path}
                      data-nav-path={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className={`w-full flex items-center gap-3 ps-7 pe-4 py-2 transition-colors ${
                        isActive(item.path)
                          ? 'bg-emerald-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <span className="relative shrink-0">
                        <item.icon size={18} />
                        {showDot && (
                          <span className="absolute -top-1 -end-1 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-gray-900" />
                        )}
                      </span>
                      <span className="text-sm truncate">{t(item.labelKey)}</span>
                      {showDot && (
                        <span className="ms-auto w-2 h-2 bg-amber-400 rounded-full shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="border-t border-gray-800 p-3 shrink-0">
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-2.5 text-gray-400 hover:bg-red-600/20 hover:text-red-400 rounded-lg transition-colors"
          title={collapsed ? t('logout') : undefined}
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span className="text-sm">{t('logout')}</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`bg-gray-900 text-white transition-all duration-300 flex-col hidden lg:flex h-screen sticky top-0 shrink-0 ${
          collapsed ? 'w-20' : 'w-64'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 start-0 z-50 bg-gray-900 text-white w-64 flex flex-col transform transition-transform duration-300 lg:hidden ${
          mobileOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
}
